import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import PaymentDetails from '../../../components/payment-method-modal/PaymentDetails';
import { initialPaymentState } from '../../../components/payment-method-modal/AddPaymentMethodModal';
import BillingAddress from '../../../components/payment-method-modal/BillingAddress';
import { AlertSVG } from '../../../components/svgs';
import { Loader } from '../../../components';
import { RequireAlert } from '../../inventory-management/InventoryManagementCollectDeposit';
import {
  AccountRoles,
  AppRoles,
  PaymentActions,
  PaymentMethods,
  ShippingPaymentMethods,
  TransactionTypeEnum,
} from '../../../types/enums';
import { ShippingSummary } from '../../../components/insurance-form/InsuranceForm';
import { ValidateAddress } from '../TransactionCartWithdraw';
import { PaymentMethod } from '../../../types/interfaces/paymentMethod.interface';
import { SpecimenSelection } from '../../../types/interfaces';
import { CartTransactionResponse } from '../../../types/interfaces/transactionCart.interfaces';
import { getSubscriptionStatus } from '../../../api/paymentGatewayApi';
import { withdrawInventory } from '../../../api/inventoryApi';
import { deleteTransactionCartItem } from '../../../api/transactionCartApi';
import { addShippingPaymentTransactions, updateShippingPaymentTransactions } from '../../../api/shippingPaymentTransactionApi';
import { makeInstantShippingPayment, makeShippingPaymentUsingCardOnFile } from '../../../api/shippingPaymentApi';
import { placeShippingOrder } from '../../../api/shipmentsApi';
import { showToast } from '../../../services/toast.service';
import { getPaymentDetails, mapPaymentInfo } from '../../../services/payment.service';
import { PermissionService } from '../../../services/permission.service';
import { fetchTransactionCartItems } from '../../../services/transactionCart.service';
import { dollarsToCents } from '../../../services/account.service';
import { getShippingNotes } from '../../../utils/addressHelpers';
import { toastMessages } from '../../../constants/errorMessages';
import { ROUTE_PATHS } from '../../../constants/routePaths';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, NOTIFICATION_CONSTANTS, PAYMENT_CONSTANTS } from '../../../constants/common';
import './paymentInformation.scss';

interface PaymentInformationProps {
  setIsPaymentPage: (isPreview: boolean) => void;
  shippingSummary: ShippingSummary | undefined;
  totalShippingAmount: number;
  withdrawCartItem: CartTransactionResponse | undefined;
  breedDate: Date | undefined;
  rentDryVaporShipperTank: string;
  shippingAddress: ValidateAddress | undefined;
}

const PaymentInformation: FC<PaymentInformationProps> = ({
  setIsPaymentPage,
  shippingSummary,
  totalShippingAmount,
  withdrawCartItem,
  breedDate,
  rentDryVaporShipperTank,
  shippingAddress,
}) => {
  const navigate = useNavigate();
  const { accountId } = useParams();

  const user = useAppSelector(state => state.user);
  const accountRoles = useAppSelector(state => state.auth.accountContext.roles) as AccountRoles[];
  const appRoles = useAppSelector(state => state.auth.appContext.roles) as AppRoles[];
  const enabled = useAppSelector(state => state.user.enabled);
  const verified = useAppSelector(state => state.user.verified);

  const permissionService = new PermissionService(accountRoles, appRoles, enabled, verified);
  const initialFormState = { value: '', valid: false };

  const [paymentInfo, setPaymentInfo] = useState<PaymentMethod>({ ...initialPaymentState, paymentType: initialFormState });
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>({ ...initialPaymentState, paymentType: initialFormState });
  const [shippingPaymentMethod, setShippingPaymentMethod] = useState<string>('');
  const [validate, setValidate] = useState<boolean>(false);
  const [placingOrder, setPlacingOrder] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notes, setNotes] = useState<string>('');
  const [withdrawingInventory, setWithdrawingInventory] = useState<boolean>(true);
  const [makingPayment, setMakingPayment] = useState<boolean>(true);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      setIsLoading(true);
      try {
        const { data: paymentStatus } = await getSubscriptionStatus(Number(accountId));

        if (accountId && paymentStatus) {
          const isUserAdmin = permissionService.userHasRSGAdminPermission();
          const paymentData = await getPaymentDetails(Number(accountId), isUserAdmin);
          paymentData && setPaymentInfo(paymentData);
        }
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [accountId]);

  useEffect(() => {
    paymentInfo != undefined && setPaymentMethod(paymentInfo);
    if (paymentInfo?.paymentType?.value === PaymentMethods.CARD) {
      setShippingPaymentMethod(ShippingPaymentMethods.CARD_ON_FILE);
    } else {
      setShippingPaymentMethod(ShippingPaymentMethods.NEW_CARD);
      setPaymentMethod(initialPaymentState);
    }
  }, [paymentInfo]);

  useEffect(() => {
    if (shippingPaymentMethod === ShippingPaymentMethods.CARD_ON_FILE && paymentInfo) {
      setPaymentMethod(paymentInfo);
    } else if (shippingPaymentMethod === ShippingPaymentMethods.NEW_CARD) {
      setPaymentMethod(initialPaymentState);
    }
  }, [shippingPaymentMethod]);

  const checkValidity = () => {
    const { paymentType, name, address, city, region, postalCode, country, cardNumber, expiry, cvv } = paymentMethod || {};

    const isCardPayment = paymentMethod?.paymentType?.value === PaymentMethods.CARD;
    const isCardOnFile = shippingPaymentMethod === ShippingPaymentMethods.CARD_ON_FILE;
    const isMarkAsPaid = shippingPaymentMethod === ShippingPaymentMethods.MARK_AS_PAID;
    const isBillingAddressValid = address?.valid && city?.valid && region?.valid && postalCode?.valid && country?.valid;
    const isCardValid = cardNumber?.valid && expiry?.valid && (isCardOnFile || cvv?.valid);
    const hasNotes = notes.trim().length > 0;

    return (
      (paymentType?.valid && name?.valid && isBillingAddressValid && isCardPayment && isCardValid) || (isMarkAsPaid && hasNotes)
    );
  };

  const handlePlaceOrder = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!placingOrder) return;

    setPlacingOrder(false);
    setValidate(true);

    const paymentInfo = mapPaymentInfo(paymentMethod);

    if (accountId && checkValidity()) {
      try {
        let paymentResponse = null;

        const commonPayload = {
          shippingOutBoundCostCents: dollarsToCents(shippingSummary?.shippingOutBoundCost || 0),
          shippingReturnCostCents: dollarsToCents(shippingSummary?.shippingReturnCost || 0),
          tankRentalFeeCents: dollarsToCents(shippingSummary?.tankRentalFee || 0),
          totalAmountPaidCents: dollarsToCents(totalShippingAmount),
          insuredAmountCents: dollarsToCents(Number(shippingSummary?.insuranceAmount.value) || 0),
          insurancePremiumCents: dollarsToCents(shippingSummary?.insurance || 0),
          processingFeeCents: dollarsToCents(shippingSummary?.handlingProcessingFee || 0),
        };

        const shippingAddressPayload = {
          address1: shippingAddress?.streetAddress?.value.replace(/,/g, ''),
          address2: shippingAddress?.streetAddress2?.value.replace(/,/g, ''),
          city: shippingAddress?.city?.value.replace(/,/g, ''),
          state: shippingAddress?.state?.value.replace(/,/g, ''),
          zipCode: shippingAddress?.postalCode?.value.replace(/,/g, ''),
          country: shippingAddress?.country?.value.replace(/,/g, ''),
          contactPhone: shippingAddress?.phone?.value.replace(/,/g, ''),
        };

        const isCardOnFile = shippingPaymentMethod === ShippingPaymentMethods.CARD_ON_FILE;
        const isNewCard = shippingPaymentMethod === ShippingPaymentMethods.NEW_CARD;
        const isMarkAsPaid = shippingPaymentMethod === ShippingPaymentMethods.MARK_AS_PAID;

        switch (shippingPaymentMethod) {
          case ShippingPaymentMethods.CARD_ON_FILE:
            paymentResponse = await handleShippingPaymentUsingCardOnFile(commonPayload);
            break;

          case ShippingPaymentMethods.NEW_CARD:
            paymentResponse = await handleInstantShippingPayment({ ...paymentInfo, ...commonPayload });
            break;
        }

        if ((isCardOnFile || isNewCard) && paymentResponse?.data?.transactionDetails?.status === 'A') {
          const shippingPaymentId = paymentResponse.data.shippingPaymentId;
          const bulkOrderIdResponse = await handleShippingWithdrawSubmit();
          const bulkOrderId = bulkOrderIdResponse?.data;
          if (bulkOrderId && shippingPaymentId) {
            const payload = { bulkOrderId: bulkOrderId };
            await updateShippingPaymentTransactions(shippingPaymentId, payload);
            await placeShippingOrder(bulkOrderId, Number(accountId), shippingAddressPayload);
            navigate(ROUTE_PATHS.APP_TRANSACTION_CART);
          }
        } else if (isMarkAsPaid) {
          const bulkOrderIdResponse = await handleShippingWithdrawSubmit();
          const bulkOrderId = bulkOrderIdResponse?.data;
          if (bulkOrderId) {
            const payload = { ...commonPayload, bulkOrderId: bulkOrderId, accountId: accountId, notes: notes };

            await addShippingPaymentTransactions(payload);
            await placeShippingOrder(bulkOrderId, Number(accountId), shippingAddressPayload);
            navigate(ROUTE_PATHS.APP_TRANSACTION_CART);
          }
        }
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        setValidate(false);
      } finally {
        setPlacingOrder(true);
      }
    } else {
      setPlacingOrder(true);
    }
  };

  const handleShippingPaymentUsingCardOnFile = async (commonPayload: any) => {
    if (!makingPayment) return;
    setMakingPayment(false);

    try {
      const response = await makeShippingPaymentUsingCardOnFile(Number(accountId), commonPayload);
      handlePaymentResponse(response);
      return response;
    } catch {
      showToast.error(toastMessages.PAYMENT_FAIL);
      setValidate(false);
      return null;
    } finally {
      setMakingPayment(true);
    }
  };

  const handleInstantShippingPayment = async (paymentInfo?: any) => {
    if (!makingPayment) return;
    setMakingPayment(false);

    try {
      const response = await makeInstantShippingPayment(Number(accountId), paymentInfo);
      handlePaymentResponse(response);
      return response;
    } catch (error: any) {
      if (error?.response?.status === 701) {
        showToast.error(error.response?.data?.title);
      } else {
        showToast.error(toastMessages.PAYMENT_FAIL);
      }
      setValidate(false);
      return null;
    } finally {
      setMakingPayment(true);
    }
  };

  const handlePaymentResponse = (response: any) => {
    if (response?.data?.transactionDetails?.status === 'A') {
      showToast.success(toastMessages.PAYMENT_SUCCESS);
    } else {
      showToast.error(toastMessages.PAYMENT_FAIL);
    }
  };

  const handleShippingWithdrawSubmit = async () => {
    if (!withdrawingInventory) return;

    setWithdrawingInventory(false);
    setValidate(true);

    const specimensSelected = Array.from(new Set(withdrawCartItem?.specimenCartItems.map(specimen => specimen.animalId))).map(
      animalId => {
        return {
          specimens: withdrawCartItem?.specimenCartItems
            .filter(specimen => specimen.animalId === animalId)
            .map(specimen => {
              if (specimen) {
                return { specimenId: specimen.specimenId, quantity: specimen.quantity };
              }
            }),
        };
      },
    );

    const withdrawRequestBody = {
      animalSelections: specimensSelected as { specimens: (SpecimenSelection | undefined)[] }[],
      breedDate: breedDate!,
      notes:
        notes +
        '\nBreed Date: ' +
        breedDate?.toLocaleDateString('en-US') +
        ('\nRent a dry vapor shipper tank from RSG for shipping order: ' + rentDryVaporShipperTank) +
        getShippingNotes(shippingAddress),
    };

    try {
      const response = await withdrawInventory(withdrawRequestBody, accountId!);
      showToast.success(toastMessages.WITHDRAW_SUBMITTED);
      await deleteTransactionCartItem(user?.userId, Number(accountId), TransactionTypeEnum.Withdraw);
      handleTransactionCart();
      return response;
    } catch {
      showToast.error(toastMessages.WITHDRAW_SUBMIT_FAIL);
      return undefined;
    } finally {
      setWithdrawingInventory(true);
    }
  };

  const handleTransactionCart = () => {
    permissionService.userHasRSGAdminPermission()
      ? fetchTransactionCartItems(user.userId)
      : fetchTransactionCartItems(user.userId, Number(accountId));
  };

  return (
    <div className="shipping-payment-information add-payment-method">
      <div className="payment-info-header">
        <h2>{PAYMENT_CONSTANTS.PAYMENT_INFORMATION}</h2>
      </div>
      {paymentInfo?.paymentType?.value === PaymentMethods.ACH && (
        <div className="payment-info-subheader ach-warning">
          <AlertSVG />
          <label>{NOTIFICATION_CONSTANTS.ACH_PAYMENT_NOT_ALLOWED}</label>
        </div>
      )}
      {isLoading ? (
        <Loader loaderSize="small" addedSpace />
      ) : (
        <form action="submit" onSubmit={(event: React.FormEvent) => handlePlaceOrder(event)}>
          <div className="payment-method-selection">
            {paymentInfo?.paymentType?.value != PaymentMethods.ACH && <label>{LABEL_CONSTANTS.CHOOSE_PAYMENT_MODE}</label>}
            <>
              <div className="radio-row">
                {paymentInfo?.paymentType?.value === PaymentMethods.CARD && (
                  <>
                    <input
                      type="radio"
                      id="payment-method-card-on-file"
                      name="payment-method-card-on-file"
                      value={shippingPaymentMethod}
                      onChange={() => setShippingPaymentMethod(ShippingPaymentMethods.CARD_ON_FILE)}
                      checked={shippingPaymentMethod === ShippingPaymentMethods.CARD_ON_FILE}
                    />
                    <label htmlFor="payment-method-card-on-file">{LABEL_CONSTANTS.USE_CARD_ON_FILE}</label>
                  </>
                )}
                {paymentInfo?.paymentType?.value === PaymentMethods.ACH && !permissionService.userHasRSGAdminPermission() ? (
                  <></>
                ) : (
                  <>
                    <input
                      type="radio"
                      id="payment-method-new-card"
                      name="payment-method-new-card"
                      value={shippingPaymentMethod}
                      onChange={() => setShippingPaymentMethod(ShippingPaymentMethods.NEW_CARD)}
                      checked={shippingPaymentMethod === ShippingPaymentMethods.NEW_CARD}
                    />
                    <label htmlFor="payment-method-new-card">{LABEL_CONSTANTS.ENTER_NEW_CARD_DETAILS}</label>
                  </>
                )}
                {permissionService.userHasRSGAdminPermission() && (
                  <>
                    <input
                      type="radio"
                      id="payment-method-mark-as-paid"
                      name="payment-method-mark-as-paid"
                      value={shippingPaymentMethod}
                      onChange={() => setShippingPaymentMethod(ShippingPaymentMethods.MARK_AS_PAID)}
                      checked={shippingPaymentMethod === ShippingPaymentMethods.MARK_AS_PAID}
                    />
                    <label htmlFor="payment-method-mark-as-paid">{LABEL_CONSTANTS.MARK_AS_PAID}</label>
                  </>
                )}
              </div>
            </>
          </div>
          {(!placingOrder || !makingPayment) && <Loader loaderSize={'medium'} />}

          {shippingPaymentMethod === ShippingPaymentMethods.MARK_AS_PAID ? (
            <div className="mark-as-paid">
              <label className="mark-as-paid-question">{PAYMENT_CONSTANTS.CONFIRM_SKIP_PAYMENT_MESSAGE}</label>
              <div className="mark-as-paid-info">
                <p>By selecting this option:</p>
                <ul>
                  <li>{PAYMENT_CONSTANTS.SKIP_PAYMENT_NO_CARD_REQUIRED}</li>
                  <li>{PAYMENT_CONSTANTS.SKIP_PAYMENT_ASSUMED_COMPLETED}</li>
                  <li>{PAYMENT_CONSTANTS.SKIP_PAYMENT_NO_CHARGES_PROCESSED}</li>
                  <li>{PAYMENT_CONSTANTS.NO_INVOICE_PAYMENT_RECORD_IN_QB}</li>
                </ul>
              </div>
              <div className="form-row expanded-text-area">
                <label>
                  *{LABEL_CONSTANTS.NOTES}:
                  <RequireAlert isActive={validate && !notes} />
                </label>
                <textarea value={notes} placeholder="Notes" onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
          ) : (
            <>
              <PaymentDetails
                paymentMethod={paymentMethod}
                validate={validate}
                buttonAction={PaymentActions.SHIPPING_PAYMENT}
                paymentMethodType={shippingPaymentMethod}
                setPaymentMethod={setPaymentMethod}
              />
              <BillingAddress
                paymentMethod={paymentMethod}
                validate={validate}
                buttonAction={PaymentActions.SHIPPING_PAYMENT}
                paymentMethodType={shippingPaymentMethod}
                setPaymentMethod={setPaymentMethod}
              />
              <div className="payment-transaction-note">
                {paymentMethod?.paymentType?.value === PaymentMethods.CARD && (
                  <div className="transaction-alert">
                    <AlertSVG />
                    <p>{NOTIFICATION_CONSTANTS.CC_PROCESSING_FEE_MSG}</p>
                  </div>
                )}
              </div>
            </>
          )}
          <div className="buttons">
            <button type="button" className="button red inverted medium" onClick={() => setIsPaymentPage(false)}>
              {BUTTON_CONSTANTS.CANCEL}
            </button>
            <button type="submit" className={`button green medium ${checkValidity() ? '' : 'disabled'}`}>
              {BUTTON_CONSTANTS.PLACE_ORDER}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
export default PaymentInformation;
